import { useState } from "react";
import { Quote, Star, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import { PROFILE } from "../data/content";
import { TESTIMONIALS } from "../data/testimonials";

export const Depoimentos = () => {
  const [data] = useState(TESTIMONIALS);

  return (
    <section
      id="depoimentos"
      data-testid="testimonials-section"
      className="relative py-20 md:py-28 bg-gradient-to-b from-background via-gold-50/40 to-background overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-gold-500/20 blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-royal-800/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/15 border border-gold-500/40">
              <Star size={12} className="text-gold-600" fill="currentColor" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-gold-700 font-semibold">
                Depoimentos verificados
              </span>
            </div>
            <h2 className="mt-4 font-serif-display text-3xl md:text-5xl text-royal-900 leading-tight">
              O que dizem os <br /> pacientes.
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className="flex items-center gap-2 text-gold-600">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={20} fill="currentColor" />
              ))}
              <span className="ml-2 font-serif-display text-3xl text-royal-900">
                {data.average_rating?.toFixed(1)}
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong className="text-royal-800">{data.total} opiniões reais</strong> no Doctoralia
            </div>
          </div>
        </div>

        <div className="mt-12 px-0 md:px-10">
          <Carousel
            opts={{ align: "start", loop: true }}
            data-testid="testimonials-carousel"
          >
            <CarouselContent>
              {data.items.map((t) => (
                <CarouselItem
                  key={t.id}
                  data-testid={`testimonial-${t.id}`}
                  className="md:basis-1/2 lg:basis-1/3"
                >
                  <div className="h-full p-6 md:p-8 rounded-xl bg-white border-2 border-border hover:border-gold-500/60 hover:shadow-xl hover:shadow-royal-900/10 transition-all duration-300 flex flex-col relative">
                    <div className="absolute top-6 right-6 flex gap-0.5 text-gold-500">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => (
                        <Star key={i} size={12} fill="currentColor" />
                      ))}
                    </div>
                    <Quote className="text-royal-800/25" size={36} />
                    <p className="mt-4 text-foreground/90 text-[15px] leading-relaxed italic flex-1">
                      “{t.text}”
                    </p>
                    <div className="mt-6 pt-5 border-t border-border flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-royal-800 to-royal-900 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                        {t.initials}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-royal-900">
                          {t.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t.service} • {t.date}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious
                data-testid="testimonial-prev"
                className="bg-white text-royal-800 border-royal-800/30 hover:bg-royal-800 hover:text-white"
              />
              <CarouselNext
                data-testid="testimonial-next"
                className="bg-white text-royal-800 border-royal-800/30 hover:bg-royal-800 hover:text-white"
              />
            </div>
          </Carousel>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 text-center">
          <Button
            asChild
            data-testid="testimonials-doctoralia-link"
            size="lg"
            className="bg-royal-800 hover:bg-royal-700 text-white h-12 px-7 rounded-md"
          >
            <a
              href={PROFILE.doctoralia_reviews}
              target="_blank"
              rel="noreferrer"
            >
              Ver todos os {data.total} depoimentos no Doctoralia
              <ExternalLink className="ml-2" size={16} />
            </a>
          </Button>
          <p className="text-xs text-muted-foreground">
            Todas as opiniões são verificadas pelo Doctoralia — sem edição e sem pagamento.
          </p>
        </div>
      </div>
    </section>
  );
};
